import { Injectable, signal } from '@angular/core';

/**
 * Show/hide state for the left filter rail.
 *
 * Why a service rather than a field on RailHostComponent: the BASKET tray
 * (`app-selection-tray`) is a SIBLING of the rail host inside `.rail-col`, so
 * the collapsed class has to be applied by `App` on the column while the toggle
 * button lives inside the host. Two components, one piece of state.
 *
 * A signal, not a plain boolean like `SidebarService.collapsed`: this value is
 * read from a template binding in a component that does not own it, and the UI
 * is zoneless, so a plain field would not reliably re-render `App`.
 *
 * Persisted, because it is a display preference and not working state -- the
 * same call the carrier-display and grid-font-size settings make. Deliberately
 * localStorage rather than sessionStorage: an analyst who hides the filters to
 * get grid width expects them to stay hidden tomorrow.
 */
@Injectable({ providedIn: 'root' })
export class RailLayoutService {
  /** `bn.` prefix matches bn.rates.carrierDisplay / bn.grids.gridFontSize. */
  private static readonly STORAGE_KEY = 'bn.rail.collapsed';

  private readonly _collapsed = signal<boolean>(RailLayoutService.readStored());
  readonly collapsed = this._collapsed.asReadonly();

  toggle(): void {
    this.setCollapsed(!this._collapsed());
  }

  setCollapsed(v: boolean): void {
    this._collapsed.set(v);
    try {
      localStorage.setItem(RailLayoutService.STORAGE_KEY, v ? '1' : '0');
    } catch {
      /* storage unavailable (private window, blocked site data) -- the rail
         still toggles, it just won't remember next time. */
    }
  }

  /** Defaults to EXPANDED. A first-time user must see the filters; a rail that
   *  starts hidden reads as a page with no filtering at all. */
  private static readStored(): boolean {
    try {
      return localStorage.getItem(RailLayoutService.STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  }
}
